<!--
  AdminSupport.svelte — Settings → Admin → Support.

  Four things configure the desk, in the order an administrator sets it up:
  which products take tickets (and the prefix their keys carry), who the agents
  are and where unassigned alerts go, the service levels per priority, and the
  categories customers file under.

  The per-product switch writes through the ordinary application update
  endpoint, as the licensing page does — these are columns on `applications`,
  and a second write path for them would be a second thing to keep in step.
  Support requires licensing (eligibility is read from the licensing backend)
  and a prefix; the server enforces both, and this page says so up front rather
  than letting the save fail.

  SLA targets are edited in hours, since that is how anyone thinks about them,
  and stored in minutes. A blank target is no target.

  Routing — who is notified of a new ticket and who it is auto-assigned to —
  is set per product (the Routing column) and per category × product (the
  rows a category expands into). Both open the same SupportRoutingEditor; the
  rule a row shows is looked up in the one list the server returns, where an
  absent rule is an empty one.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Badge,
		Button,
		Input,
		Select,
		Spinner,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell,
		Toggle
	} from 'flowbite-svelte';
	import {
		ConfirmButton,
		EmptyState,
		FormField,
		LicenseAppIcon,
		Panel,
		RoutedLink,
		SupportRoutingEditor
	} from '../../components';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import {
		createSupportCategory,
		deleteSupportCategory,
		getSupportSettings,
		isValidEmail,
		listAllSupportCategories,
		listApplications,
		listRoles,
		listSupportAgents,
		listSupportRouting,
		updateApplication,
		updateSupportCategory,
		updateSupportSettings
	} from '../../AppNetwork';
	import type {
		Application,
		Role,
		SupportCategory,
		SupportPerson,
		SupportRoutingRule,
		SupportSettings,
		SupportTicketPriority
	} from '../../AppTypes';
	import {
		SUPPORT_PRIORITIES,
		isValidTicketPrefix,
		slaHoursToMinutes,
		slaMinutesToHours,
		supportPriorityLabel
	} from '../../support/supportFormat';
	import { toastError, toastSuccess } from '../../toast';
	import { t, t2 } from '../../i18n';

	/** One product's editable support configuration. */
	interface ProductRow {
		/** The application. */
		app: Application;
		/** Whether support is switched on. */
		enabled: boolean;
		/** The ticket-key prefix being edited. */
		prefix: string;
		/** Whether a save is in flight. */
		saving: boolean;
	}

	/** One priority's SLA targets, as the hours an administrator edits. */
	interface SLARow {
		/** The priority. */
		priority: SupportTicketPriority;
		/** First-response target in hours; blank is none. */
		firstResponse: string;
		/** Resolution target in hours; blank is none. */
		resolution: string;
	}

	/** Whether the page is loading. */
	let loading = $state(true);
	/** Failure of the load. */
	let error = $state('');

	/** Roles, for the support-role picker. */
	let roles: Role[] = $state([]);
	/** Support role; empty makes admins the only agents. */
	let supportRoleUUID = $state('');
	/** Team notification address. */
	let notifyEmail = $state('');
	/** Community forum offered to users without support; blank offers none. */
	let communityURL = $state('');
	/** Discord invite offered to users without support; blank offers none. */
	let discordURL = $state('');
	/** Days before a ticket awaiting the customer resolves itself; 0 is never. */
	let autoResolveDays = $state('7');
	/** Days before a resolved ticket closes itself; 0 is never. */
	let autoCloseDays = $state('14');
	/** SLA rows, one per priority. */
	let slaRows: SLARow[] = $state([]);
	/** Whether the settings are being saved. */
	let savingSettings = $state(false);

	/** One row per application. */
	let productRows: ProductRow[] = $state([]);

	/** Every category, inactive included. */
	let categories: SupportCategory[] = $state([]);
	/** Whether the category editor is open. */
	let editingCategory = $state(false);
	/** The category being edited, or `null` for a new one. */
	let categoryUUID = $state<string | null>(null);
	/** Category editor: name. */
	let categoryName = $state('');
	/** Category editor: description. */
	let categoryDescription = $state('');
	/** Category editor: product scope; empty is every product. */
	let categoryScope = $state('');
	/** Category editor: sort order. */
	let categoryOrder = $state('0');
	/** Category editor: offered for new tickets. */
	let categoryActive = $state(true);
	/** Whether a category save is in flight. */
	let savingCategory = $state(false);

	/** The platform-wide switch, as saved. */
	let supportEnabled = $state(true);
	/** Whether a switch change is in flight. */
	let switching = $state(false);

	/**
	 * Flips the platform-wide switch. Saved on its own, at once: it is the one
	 * setting whose effect people look for immediately, and it must not wait on
	 * — or be blocked by — an unrelated half-edited field in the form below.
	 */
	async function setSupportEnabled(next: boolean): Promise<void> {
		switching = true;
		/** The stored value comes back; the toggle follows it rather than the click. */
		try {
			supportEnabled = (await updateSupportSettings({ supportEnabled: next })).supportEnabled;
			toastSuccess(supportEnabled ? t2('Support is turned on.') : t2('Support is turned off.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save support settings'));
		} finally {
			switching = false;
		}
	}

	/** Copies saved settings into the form. */
	function applySettings(settings: SupportSettings): void {
		supportEnabled = settings.supportEnabled;
		supportRoleUUID = settings.supportRoleUUID ?? '';
		notifyEmail = settings.supportNotifyEmail ?? '';
		communityURL = settings.supportCommunityURL ?? '';
		discordURL = settings.supportDiscordURL ?? '';
		autoResolveDays = String(settings.supportAutoResolveDays);
		autoCloseDays = String(settings.supportAutoCloseDays);
		slaRows = SUPPORT_PRIORITIES.map((priority) => ({
			priority,
			firstResponse: slaMinutesToHours(settings.supportSLAPolicy[priority]?.firstResponseMinutes ?? null),
			resolution: slaMinutesToHours(settings.supportSLAPolicy[priority]?.resolutionMinutes ?? null)
		}));
	}

	/** Builds the editable row for one application. */
	function toProductRow(app: Application): ProductRow {
		return { app, enabled: app.isSupportEnabled, prefix: app.supportTicketPrefix ?? '', saving: false };
	}

	/** Every routing rule with at least one member; a rule that is absent is empty. */
	let routing: SupportRoutingRule[] = $state([]);
	/** Every agent — everyone who can be in a routing rule. */
	let agents: SupportPerson[] = $state([]);
	/** The rule whose editor is open, or `null` before one was first opened. */
	let routingTarget: { app: Application; category: SupportCategory | null } | null = $state(null);
	/** Whether the routing editor is open. */
	let routingEditorOpen = $state(false);
	/** Categories whose per-product routing rows are expanded, by category UUID. Not persisted. */
	let expandedCategories: Record<string, boolean> = $state({});

	/** The saved rule for a product, or a category on it; `null` when it has no members. */
	function ruleFor(applicationUUID: string, categoryUUID: string | null): SupportRoutingRule | null {
		return (
			routing.find((rule) => rule.applicationUUID === applicationUUID && rule.categoryUUID === categoryUUID) ?? null
		);
	}

	/** Opens the routing editor on a product rule, or a category × product rule. */
	function editRouting(app: Application, category: SupportCategory | null): void {
		routingTarget = { app, category };
		routingEditorOpen = true;
	}

	/** Puts a saved rule in place of the one it replaces; an emptied rule simply goes. */
	function applyRoutingRule(saved: SupportRoutingRule): void {
		routing = [
			...routing.filter(
				(rule) => !(rule.applicationUUID === saved.applicationUUID && rule.categoryUUID === saved.categoryUUID)
			),
			...(saved.members.length > 0 ? [saved] : [])
		];
	}

	/** Shows or hides one category's per-product routing rows. */
	function toggleCategory(categoryUUID: string): void {
		expandedCategories = { ...expandedCategories, [categoryUUID]: !expandedCategories[categoryUUID] };
	}

	/**
	 * The products a category's routing can be set for: its own product when it
	 * is scoped to one, every support-enabled product when it is global. Read
	 * from what is saved, not from a half-edited support switch.
	 */
	function categoryProducts(category: SupportCategory): Application[] {
		const apps = productRows.map((row) => row.app);
		/* A scoped category routes on its own product only — if that still exists. */
		if (category.applicationUUID) return apps.filter((app) => app.applicationUUID === category.applicationUUID);
		return apps.filter((app) => app.isSupportEnabled);
	}

	/** Loads settings, roles, applications and categories. */
	async function load(): Promise<void> {
		loading = true;
		error = '';
		/** All four are needed to render the page, so they load together. */
		try {
			const [settings, roleList, apps, categoryList, routingRules, agentList] = await Promise.all([
				getSupportSettings(),
				listRoles(),
				listApplications({ limit: 500 }),
				listAllSupportCategories(),
				listSupportRouting(),
				listSupportAgents()
			]);
			applySettings(settings);
			roles = roleList;
			productRows = apps.map(toProductRow);
			categories = categoryList;
			routing = routingRules;
			agents = agentList;
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load support settings');
		} finally {
			loading = false;
		}
	}

	/** A day count from its field, or `null` when it is not a whole number of days. */
	function parseDays(text: string): number | null {
		const value = Number(text);
		return Number.isInteger(value) && value >= 0 ? value : null;
	}

	/** Validation messages for the settings form; empty when fine. */
	let settingsErrors = $derived({
		notifyEmail: notifyEmail.trim() && !isValidEmail(notifyEmail.trim()) ? $t('Enter a valid email address.') : '',
		autoResolveDays: parseDays(autoResolveDays) === null ? $t('Enter a whole number of days.') : '',
		autoCloseDays: parseDays(autoCloseDays) === null ? $t('Enter a whole number of days.') : '',
		sla: slaRows.some(
			(row) => slaHoursToMinutes(row.firstResponse) === undefined || slaHoursToMinutes(row.resolution) === undefined
		)
			? $t('SLA targets must be positive numbers of hours, or blank for none.')
			: ''
	});

	/** Whether the settings form can be saved. */
	let settingsValid = $derived(Object.values(settingsErrors).every((message) => message === ''));

	/** Saves the general settings and SLA targets. */
	async function saveSettings(): Promise<void> {
		/* The errors are on screen beside each field. */
		if (!settingsValid) return;
		savingSettings = true;
		const policy = Object.fromEntries(
			slaRows.map((row) => [
				row.priority,
				{
					firstResponseMinutes: slaHoursToMinutes(row.firstResponse) ?? null,
					resolutionMinutes: slaHoursToMinutes(row.resolution) ?? null
				}
			])
		) as SupportSettings['supportSLAPolicy'];
		/** The response is the stored settings, which the form is reset to. */
		try {
			applySettings(
				await updateSupportSettings({
					supportRoleUUID: supportRoleUUID || null,
					supportNotifyEmail: notifyEmail.trim() || null,
					supportCommunityURL: communityURL.trim() || null,
					supportDiscordURL: discordURL.trim() || null,
					supportAutoResolveDays: parseDays(autoResolveDays) as number,
					supportAutoCloseDays: parseDays(autoCloseDays) as number,
					supportSLAPolicy: policy
				})
			);
			toastSuccess(t2('Support settings saved.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save support settings'));
		} finally {
			savingSettings = false;
		}
	}

	/** Whether a product row differs from what is saved. */
	function isDirty(row: ProductRow): boolean {
		return row.enabled !== row.app.isSupportEnabled || row.prefix !== (row.app.supportTicketPrefix ?? '');
	}

	/** Why a product row cannot be saved as it stands, or empty when it can. */
	function productProblem(row: ProductRow): string {
		/* Eligibility is read from the licensing backend; without one there is nothing to read. */
		if (row.enabled && !row.app.isLicensingEnabled) return t2('Turn on licensing for this product first.');
		/* A prefix is optional until support is on, but must be well formed whenever given. */
		if ((row.enabled || row.prefix) && !isValidTicketPrefix(row.prefix)) {
			return t2('The prefix must be 2–10 uppercase letters or digits.');
		}
		/* Prefixes are unique, so a clash is caught here rather than by the save. */
		if (row.prefix && productRows.some((other) => other !== row && other.prefix === row.prefix)) {
			return t2('Another product already uses this prefix.');
		}
		return '';
	}

	/** Saves one product's support switch and prefix. */
	async function saveProduct(row: ProductRow): Promise<void> {
		row.saving = true;
		/** The ordinary application update; the server re-checks licensing and prefix. */
		try {
			row.app = await updateApplication(row.app.id, {
				isSupportEnabled: row.enabled,
				supportTicketPrefix: row.prefix || null
			});
			row.enabled = row.app.isSupportEnabled;
			row.prefix = row.app.supportTicketPrefix ?? '';
			toastSuccess(t2('Support saved for {{name}}.', { name: row.app.applicationName }));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save support for this product'));
		} finally {
			row.saving = false;
		}
	}

	/** Opens the category editor, empty or on an existing category. */
	function openCategory(category: SupportCategory | null): void {
		categoryUUID = category?.categoryUUID ?? null;
		categoryName = category?.name ?? '';
		categoryDescription = category?.description ?? '';
		categoryScope = category?.applicationUUID ?? '';
		categoryOrder = String(category?.sortOrder ?? categories.length);
		categoryActive = category?.isActive ?? true;
		editingCategory = true;
	}

	/** Saves the category editor — a create or an update. */
	async function saveCategory(): Promise<void> {
		savingCategory = true;
		const input = {
			name: categoryName.trim(),
			description: categoryDescription.trim() || null,
			applicationUUID: categoryScope || null,
			sortOrder: parseDays(categoryOrder) ?? 0,
			isActive: categoryActive
		};
		/** Create or update, then re-read so the order is the server's. */
		try {
			await (categoryUUID ? updateSupportCategory(categoryUUID, input) : createSupportCategory(input));
			categories = await listAllSupportCategories();
			editingCategory = false;
			toastSuccess(t2('Category saved.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save the category'));
		} finally {
			savingCategory = false;
		}
	}

	/** Deletes one category. */
	async function removeCategory(category: SupportCategory): Promise<void> {
		/** Removed from the list only once the server confirms. */
		try {
			await deleteSupportCategory(category.categoryUUID);
			categories = categories.filter((c) => c.categoryUUID !== category.categoryUUID);
			toastSuccess(t2('Category deleted.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete the category'));
		}
	}

	/** The name of the product a category is scoped to. */
	function scopeName(applicationUUID: string | null): string {
		/* Unscoped categories are offered on every product. */
		if (!applicationUUID) return t2('All products');
		return (
			productRows.find((row) => row.app.applicationUUID === applicationUUID)?.app.applicationName ?? applicationUUID
		);
	}

	/** Role picker items. */
	let roleItems = $derived([
		{ value: '', name: $t('No support role — administrators only') },
		...roles.map((role) => ({ value: role.roleUUID, name: role.name }))
	]);

	/** Product scope items for categories. */
	let scopeItems = $derived([
		{ value: '', name: $t('All products') },
		...productRows
			.filter((row) => row.app.isSupportEnabled)
			.map((row) => ({ value: row.app.applicationUUID, name: row.app.applicationName }))
	]);

	onMount(load);
</script>

<div class="space-y-6">
	<div>
		<h3 class="text-xl font-semibold text-gray-900 dark:text-white">{$t('Support Desk')}</h3>
		<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
			{$t('Customers file tickets against a product while their license for it includes support. Agents answer them from the Support Desk.')}
		</p>
	</div>

	{#if loading}
		<EmptyState variant="loading" />
	{:else if error}
		<EmptyState variant="error" description={error} />
	{:else}
		<AbstractCard class="max-w-none">
			<div class="flex flex-wrap items-center gap-4">
				<div class="min-w-0 flex-1">
					<p class="font-semibold text-gray-900 dark:text-white">{$t('Support desk')}</p>
					<p class="text-default text-sm">
						{supportEnabled
							? $t('On. Customers whose license includes support can open tickets, and agents answer them from the Support Desk.')
							: $t('Off. Support is hidden from customers and agents and the automatic sweeps are paused. Tickets and settings are kept.')}
					</p>
				</div>
				<Toggle
					checked={supportEnabled}
					disabled={switching}
					onchange={(event: Event) => setSupportEnabled((event.currentTarget as HTMLInputElement).checked)}
					aria-label={$t('Support desk enabled')}
				/>
			</div>
		</AbstractCard>

		<Panel title={$t('Products')} faIcon="cubes">
			<p class="text-default mb-3 text-sm">
				{$t('Support needs licensing turned on for the product, and a ticket prefix — the LOD in LOD-1042.')}
			</p>
			<Table divClass="widget !p-1 relative" class="dataTable">
				<TableHead>
					<TableHeadCell>{$t('Application')}</TableHeadCell>
					<TableHeadCell class="w-28">{$t('Licensing')}</TableHeadCell>
					<TableHeadCell class="w-40">{$t('Prefix')}</TableHeadCell>
					<TableHeadCell class="w-28">{$t('Support')}</TableHeadCell>
					<TableHeadCell>{$t('Routing')}</TableHeadCell>
					<TableHeadCell class="w-28"><span class="sr-only">{$t('Actions')}</span></TableHeadCell>
				</TableHead>
				<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each productRows as row (row.app.id)}
						{@const problem = productProblem(row)}
						<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600" data-app={row.app.applicationUUID}>
							<TableBodyCell>
								<div class="flex items-center gap-2">
									<LicenseAppIcon
										iconImageName={row.app.iconImageName}
										icon={row.app.icon}
										name={row.app.applicationName}
										class="h-4 w-4"
									/>
									<RoutedLink to="/Applications/{row.app.id}">
										<span class="font-medium">{row.app.applicationName}</span>
									</RoutedLink>
								</div>
								{#if problem && isDirty(row)}
									<p class="mt-1 text-xs text-red-600 dark:text-red-400">{problem}</p>
								{/if}
							</TableBodyCell>
							<TableBodyCell>
								{#if row.app.isLicensingEnabled}
									<Badge color="green">{$t('On')}</Badge>
								{:else}
									<Badge color="gray">{$t('Off')}</Badge>
								{/if}
							</TableBodyCell>
							<TableBodyCell>
								<Input
									size="sm"
									class="!w-28 font-mono uppercase"
									value={row.prefix}
									maxlength={10}
									placeholder="LOD"
									aria-label={$t('Ticket prefix for {{name}}', { name: row.app.applicationName })}
									oninput={(event: Event) =>
										(row.prefix = (event.currentTarget as HTMLInputElement).value.toUpperCase().trim())}
								/>
							</TableBodyCell>
							<TableBodyCell>
								<Toggle
									bind:checked={row.enabled}
									disabled={!row.app.isLicensingEnabled && !row.enabled}
									aria-label={$t('Support for {{name}}', { name: row.app.applicationName })}
								/>
							</TableBodyCell>
							<TableBodyCell>
								<!-- Who hears about, and who is handed, every new ticket on this product. -->
								<div class="flex flex-wrap items-center gap-1">
									{@render routingChips(ruleFor(row.app.applicationUUID, null), $t('Team address only'))}
									<Button
										size="xs"
										color="alternative"
										class="!p-1.5"
										onclick={() => editRouting(row.app, null)}
										aria-label={$t('Edit routing for {{name}}', { name: row.app.applicationName })}
									>
										<i class="fa-solid fa-pen-to-square h-3 w-3"></i>
									</Button>
								</div>
							</TableBodyCell>
							<TableBodyCell>
								<Button
									size="xs"
									disabled={!isDirty(row) || !!problem || row.saving}
									onclick={() => saveProduct(row)}
									data-testid="save-product"
								>
									{#if row.saving}<Spinner size="4" class="me-1" />{/if}{$t('Save')}
								</Button>
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</Panel>

		<Panel title={$t('Agents and automation')} faIcon="headset">
			<div class="grid gap-x-6 md:grid-cols-2">
				<FormField
					label={$t('Support role')}
					id="support-role"
					description={$t('Holders of this role are agents. Administrators always are.')}
				>
					<Select
						id="support-role"
						placeholder=""
						items={roleItems}
						value={supportRoleUUID}
						onchange={(event: Event) => (supportRoleUUID = (event.currentTarget as HTMLSelectElement).value)}
					/>
				</FormField>
				<FormField
					label={$t('Team email')}
					id="support-notify"
					description={$t('Receives SLA breach alerts for tickets nobody is assigned to.')}
					error={settingsErrors.notifyEmail}
				>
					<Input id="support-notify" type="email" bind:value={notifyEmail} placeholder="support@example.com" />
				</FormField>
				<FormField
					label={$t('Auto-resolve after (days)')}
					id="support-auto-resolve"
					description={$t('A ticket waiting on the customer this long is resolved. 0 turns this off.')}
					error={settingsErrors.autoResolveDays}
				>
					<Input id="support-auto-resolve" type="number" min="0" class="!w-24" bind:value={autoResolveDays} />
				</FormField>
				<FormField
					label={$t('Auto-close after (days)')}
					id="support-auto-close"
					description={$t('A resolved ticket untouched this long is closed. 0 turns this off.')}
					error={settingsErrors.autoCloseDays}
				>
					<Input id="support-auto-close" type="number" min="0" class="!w-24" bind:value={autoCloseDays} />
				</FormField>
				<FormField
					label={$t('Community URL')}
					id="support-community"
					description={$t('Offered to users without access to support. Leave blank to offer none.')}
				>
					<Input id="support-community" type="url" bind:value={communityURL} placeholder="https://community.abstract3d.com/" />
				</FormField>
				<FormField
					label={$t('Discord invite')}
					id="support-discord"
					description={$t('Offered to users without access to support. Leave blank to offer none.')}
				>
					<Input id="support-discord" type="url" bind:value={discordURL} placeholder="https://discord.gg/…" />
				</FormField>
			</div>
		</Panel>

		<Panel title={$t('Service levels')} faIcon="stopwatch">
			<p class="text-default mb-3 text-sm">
				{$t('Targets in hours of wall-clock time from when the ticket was opened. The resolution clock pauses while a ticket waits on the customer. Leave blank for no target.')}
			</p>
			<Table divClass="widget !p-1 relative" class="dataTable">
				<TableHead>
					<TableHeadCell>{$t('Priority')}</TableHeadCell>
					<TableHeadCell>{$t('First response (hours)')}</TableHeadCell>
					<TableHeadCell>{$t('Resolution (hours)')}</TableHeadCell>
				</TableHead>
				<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each slaRows as row (row.priority)}
						<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
							<TableBodyCell class="font-medium">{$t(supportPriorityLabel(row.priority))}</TableBodyCell>
							<TableBodyCell>
								<Input
									size="sm"
									class="!w-28"
									bind:value={row.firstResponse}
									inputmode="decimal"
									aria-label={$t('First response target for {{priority}}', {
										priority: $t(supportPriorityLabel(row.priority))
									})}
								/>
							</TableBodyCell>
							<TableBodyCell>
								<Input
									size="sm"
									class="!w-28"
									bind:value={row.resolution}
									inputmode="decimal"
									aria-label={$t('Resolution target for {{priority}}', {
										priority: $t(supportPriorityLabel(row.priority))
									})}
								/>
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
			{#if settingsErrors.sla}
				<p class="mt-2 text-xs text-red-600 dark:text-red-500">{settingsErrors.sla}</p>
			{/if}
		</Panel>

		<div class="flex justify-end">
			<Button disabled={!settingsValid || savingSettings} onclick={saveSettings}>
				{#if savingSettings}<Spinner size="4" class="me-2" />{/if}{$t('Save settings')}
			</Button>
		</div>

		<Panel title={$t('Categories')} faIcon="tags">
			{#snippet titleRight()}
				{#if !editingCategory}
					<Button size="xs" onclick={() => openCategory(null)}>
						<i class="fa-solid fa-plus me-1"></i>{$t('Add category')}
					</Button>
				{/if}
			{/snippet}

			{#if editingCategory}
				<AbstractCard class="mb-4 max-w-none">
					<div class="grid gap-x-4 md:grid-cols-[1fr_14rem_6rem]">
						<FormField label={$t('Name')} id="category-name" required>
							<Input id="category-name" bind:value={categoryName} />
						</FormField>
						<FormField label={$t('Product')} id="category-scope">
							<Select
								id="category-scope"
								placeholder=""
								items={scopeItems}
								value={categoryScope}
								onchange={(event: Event) => (categoryScope = (event.currentTarget as HTMLSelectElement).value)}
							/>
						</FormField>
						<FormField label={$t('Order')} id="category-order">
							<Input id="category-order" type="number" min="0" class="!w-20" bind:value={categoryOrder} />
						</FormField>
					</div>
					<FormField label={$t('Description')} id="category-description" description={$t('Shown as a hint when a customer picks it.')}>
						<Input id="category-description" bind:value={categoryDescription} />
					</FormField>
					<div class="flex flex-wrap items-center gap-2">
						<Toggle bind:checked={categoryActive}>{$t('Offered for new tickets')}</Toggle>
						<div class="grow"></div>
						<Button color="alternative" size="sm" onclick={() => (editingCategory = false)}>{$t('Cancel')}</Button>
						<Button size="sm" disabled={!categoryName.trim() || savingCategory} onclick={saveCategory}>
							{#if savingCategory}<Spinner size="4" class="me-2" />{/if}{$t('Save')}
						</Button>
					</div>
				</AbstractCard>
			{/if}

			{#if categories.length === 0}
				<EmptyState
					faIcon="tags"
					title={$t('No categories')}
					description={$t('Categories are optional. They help customers say what a ticket is about.')}
					compact
				/>
			{:else}
				<Table divClass="widget !p-1 relative" class="dataTable">
					<TableHead>
						<TableHeadCell>{$t('Name')}</TableHeadCell>
						<TableHeadCell class="w-40">{$t('Product')}</TableHeadCell>
						<TableHeadCell class="w-20">{$t('Order')}</TableHeadCell>
						<TableHeadCell class="w-28">{$t('Status')}</TableHeadCell>
						<TableHeadCell class="w-px !px-2"><span class="sr-only">{$t('Actions')}</span></TableHeadCell>
					</TableHead>
					<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
						{#each categories as category (category.categoryUUID)}
							{@const expanded = expandedCategories[category.categoryUUID] === true}
							<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600" data-category={category.categoryUUID}>
								<TableBodyCell>
									<div class="flex items-start gap-2">
										<button
											type="button"
											class="text-default mt-0.5 w-4 shrink-0 hover:text-gray-900 dark:hover:text-white"
											aria-expanded={expanded}
											aria-label={expanded
												? $t('Hide routing for {{name}}', { name: category.name })
												: $t('Show routing for {{name}}', { name: category.name })}
											onclick={() => toggleCategory(category.categoryUUID)}
										>
											<i class="fa-solid {expanded ? 'fa-chevron-down' : 'fa-chevron-right'} text-xs"></i>
										</button>
										<div class="min-w-0">
											<span class="font-medium">{category.name}</span>
											{#if category.description}
												<p class="text-default text-xs">{category.description}</p>
											{/if}
										</div>
									</div>
								</TableBodyCell>
								<TableBodyCell class="text-sm">{scopeName(category.applicationUUID)}</TableBodyCell>
								<TableBodyCell class="text-sm">{category.sortOrder}</TableBodyCell>
								<TableBodyCell>
									{#if category.isActive}
										<Badge color="green">{$t('Active')}</Badge>
									{:else}
										<Badge color="gray">{$t('Inactive')}</Badge>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="!px-2">
									<div class="flex gap-1">
										<Button
											size="xs"
											color="alternative"
											class="!p-2"
											title={$t('Edit')}
											aria-label={$t('Edit {{name}}', { name: category.name })}
											onclick={() => openCategory(category)}
										>
											<i class="fa-solid fa-pen-to-square h-3 w-3"></i>
										</Button>
										<ConfirmButton
											size="xs"
											color="alternative"
											class="!p-2"
											confirmText={$t('Delete the category “{{name}}”?', { name: category.name })}
											onconfirm={() => removeCategory(category)}
										>
											<i class="fa-solid fa-trash h-3 w-3 text-red-600"></i>
										</ConfirmButton>
									</div>
								</TableBodyCell>
							</TableBodyRow>
							{#if expanded}
								<!-- One second-level row per product the category routes on, inside the same
								     table so the column grid holds: the product under Name, its chips across
								     the next three columns, the edit button under Actions. -->
								{#each categoryProducts(category) as app (app.applicationUUID)}
									<TableBodyRow
										class="hover:bg-gray-50 dark:hover:bg-gray-600"
										data-category-route="{category.categoryUUID}:{app.applicationUUID}"
									>
										<TableBodyCell class="!ps-12">
											<div class="flex items-center gap-2 text-sm">
												<LicenseAppIcon
													iconImageName={app.iconImageName}
													icon={app.icon}
													name={app.applicationName}
													class="h-4 w-4"
												/>
												<span>{app.applicationName}</span>
											</div>
										</TableBodyCell>
										<TableBodyCell colspan={3}>
											<div class="flex flex-wrap items-center gap-1">
												{@render routingChips(ruleFor(app.applicationUUID, category.categoryUUID), $t('Product rule only'))}
											</div>
										</TableBodyCell>
										<TableBodyCell class="!px-2">
											<Button
												size="xs"
												color="alternative"
												class="!p-2"
												title={$t('Edit routing')}
												aria-label={$t('Edit routing for {{category}} on {{product}}', {
													category: category.name,
													product: app.applicationName
												})}
												onclick={() => editRouting(app, category)}
											>
												<i class="fa-solid fa-pen-to-square h-3 w-3"></i>
											</Button>
										</TableBodyCell>
									</TableBodyRow>
								{:else}
									<TableBodyRow>
										<TableBodyCell colspan={5} class="text-default !ps-12 text-sm">
											{$t('No support-enabled product takes this category.')}
										</TableBodyCell>
									</TableBodyRow>
								{/each}
							{/if}
						{/each}
					</TableBody>
				</Table>
			{/if}
		</Panel>
	{/if}
</div>

<!-- A rule's members as chips: a bell for notify, a user-check for auto-assign. -->
{#snippet routingChips(rule: SupportRoutingRule | null, emptyText: string)}
	{#each rule?.members ?? [] as member (member.person.userUUID)}
		<Badge color="gray" class="gap-1" data-member={member.person.userUUID}>
			<span>{member.person.name}</span>
			{#if member.notify}
				<i class="fa-solid fa-bell text-[10px]" title={$t('Notified')}></i><span class="sr-only">{$t('Notified')}</span>
			{/if}
			{#if member.autoAssign}
				<i class="fa-solid fa-user-check text-[10px]" title={$t('Auto-assigned')}></i><span class="sr-only"
					>{$t('Auto-assigned')}</span
				>
			{/if}
		</Badge>
	{:else}
		<span class="text-default text-xs">{emptyText}</span>
	{/each}
{/snippet}

{#if routingTarget}
	<SupportRoutingEditor
		bind:open={routingEditorOpen}
		applicationUUID={routingTarget.app.applicationUUID}
		productName={routingTarget.app.applicationName}
		categoryUUID={routingTarget.category?.categoryUUID ?? null}
		categoryName={routingTarget.category?.name ?? null}
		rule={ruleFor(routingTarget.app.applicationUUID, routingTarget.category?.categoryUUID ?? null)}
		{agents}
		onsaved={applyRoutingRule}
	/>
{/if}
